import { fetcher, IFetcherOptions, IGlobalRequestOptions } from "common/api/Fetcher";
import { baseCleanProps } from "common/api/Api";
import { CrmUserType } from "common/records/CrmUser";
import { CamelToSnake } from "common/helpers/Format";
import { IMetaPaginationData } from "common/records/MetaPagination";
import { IUserData, UserRecord } from "common/records/User";
import { userIncludes } from "common/api/Api";
import { IAccessData } from "common/records/Access";
import { toParams } from "common/records/index/QueryParamsBase";
import { UserParamsRecord } from "common/records/index/UserParams";

export interface ILoginData {
  token: string;
  refresh_token: string;
  user: IUserData;
  accessUid?: string;
}

export interface IUserIndexResponse {
  users: IUserData[];
  meta?: { pagination: IMetaPaginationData };
}

export interface IUserResponse {
  user: IUserData;
}

export interface IAclResponse {
  acl: { [key: string]: string[] };
  templates: { name: string; descriptor: string }[];
}
export const url = "/api/v1/users/";

export const logout = (token: string): Promise<void> => {
  const actionUrl = fetcher.joinUrls(url, "logout");

  return fetch(actionUrl, {
    method: "POST",
    body: JSON.stringify({
      token: token,
    }),
  })
    .then(fetcher.standardStatus)
    .then(() => {});
};

export const create = (user: UserRecord): Promise<IUserResponse> => {
  const postBody = { user: cleanProps(user), include: userIncludes };

  return fetcher.post<IUserResponse>(url, postBody);
};

export const update = (user: UserRecord): Promise<IUserResponse> => {
  const actionUrl = fetcher.joinUrls(url, user.id.toString());
  const postBody: any = { user: cleanProps(user), include: userIncludes };

  return fetcher.patch(actionUrl, postBody);
};

export const load = (id: number, options: IGlobalRequestOptions = {}): Promise<IUserData> => {
  let actionUrl = fetcher.joinUrls(url, id.toString());

  actionUrl = fetcher.urlOptions(actionUrl, {
    "include[]": userIncludes,
  });

  return fetcher.get<{ user: IUserData }>(actionUrl, {}, options).then((data) => data.user);
};

export const loadByEmail = (email: string): Promise<IUserResponse> => {
  let actionUrl = fetcher.joinUrls(url, "email");
  // rsf-api answers 404 for an authenticated identity that has no RSF user yet.
  const options: IFetcherOptions = { useGlobalNotFoundRedirect: false };

  actionUrl = fetcher.urlOptions(actionUrl, {
    email: email,
    "include[]": userIncludes,
  });

  return fetcher.get<{ user: IUserData }>(actionUrl, {}, options);
};

export const loadByAltId = (altName: string, altId: string, token?: string): Promise<IUserData> => {
  let actionUrl = fetcher.joinUrls(url, "by_alt_id");
  // rsf-api answers 404 for an SSO identity that has no RSF user yet, and 403 for one whose access was revoked.
  const options: IFetcherOptions = { useGlobalNotFoundRedirect: false, useGlobalAuthRedirect: false };

  actionUrl = fetcher.urlOptions(actionUrl, {
    alt_name: altName,
    alt_id: altId,
    "include[]": userIncludes,
  });

  if (token) {
    options.headers = {
      Authorization: `Bearer ${token}`,
    };
  }

  return fetcher.get<{ user: IUserData }>(actionUrl, {}, options).then((data) => data.user);
};

export const list = (queryParams: UserParamsRecord, options?: IGlobalRequestOptions): Promise<IUserIndexResponse> => {
  const parameters = toParams(queryParams);
  parameters["include[]"] = ["accesses"];

  return fetcher.get<IUserIndexResponse>(url, parameters, options);
};

export const crmUserCheck = (orgId: number, userId: number, type: CrmUserType): Promise<void> => {
  const orgAssociation = CamelToSnake(type) + "s";
  const actionUrl = `api/v1/orgs/${orgId}/users/${userId}/${orgAssociation}/check`;

  return fetcher.get(actionUrl);
};

export const revokeAccess = async (userId: number, accessId: number): Promise<void> => {
  const actionUrl = fetcher.joinUrls(url, userId.toString(), "revoke_access");

  await fetcher.post(actionUrl, { access_id: accessId });
};

export const sendInvitation = async (email: string): Promise<void> => {
  const actionUrl = fetcher.joinUrls(url, "send_invitation");

  await fetcher.post(actionUrl, { email });
};

// userId used to get org settings on the backend, SO for a new user, we use the currentUserId instead of the currrent org
export const loadAclValues = (userId: number, accessUid: string, template: string): Promise<IAclResponse> => {
  const actionUrl = fetcher.joinUrls(url, userId.toString(), "acl");

  const params: { access_uid?: string; template?: string } = {};

  if (accessUid) {
    params.access_uid = accessUid;
  }

  if (template) {
    params.template = template;
  }

  return fetcher.get(actionUrl, params);
};

const cleanProps = (user: UserRecord): Partial<IUserData> => {
  const userData: Partial<IUserData> = baseCleanProps(user.toJS());

  if (userData.accesses) {
    (userData as any).accesses_attributes = userData?.dirty_accesses?.map((a) => {
      const cleaned: Partial<IAccessData> = baseCleanProps(a);

      delete cleaned.org;

      if (cleaned.org_id && cleaned.org_id <= 0) {
        delete cleaned.org_id;
      }

      return cleaned;
    });

    delete userData.dirty_accesses;
  }

  if (userData.org) {
    delete userData.org;
  }

  return userData;
};
